/**
 * Shop AI Chat - Client-side implementation
 *
 * This module handles the chat interface for the Shopify AI Chat application.
 * It manages the UI interactions, API communication, and message rendering.
 */
(function() {
  'use strict';

  /**
   * Application namespace to prevent global scope pollution
   */
    /**
   * Backend origin for the concierge API.
   *
   * Set from the theme block setting (window.shopChatConfig.apiBaseUrl). The upstream
   * template hardcoded the local dev tunnel in three places, which meant a published
   * theme called https://localhost:3458 from the shopper's browser — every request
   * failing with nothing in the UI to explain why. Falls back to the dev URL so local
   * work is unchanged.
   */
  const DEEVE_API_BASE = (window.shopChatConfig?.apiBaseUrl || 'https://localhost:3458').replace(/\/+$/, '');


  /**
   * Placeholder for a product with no image.
   *
   * The upstream template pointed at a placeholder hosted on Shopify's own DEMO store
   * (/s/files/1/0533/2089/), which would have rendered another shop's asset inside
   * DEEVE's storefront and added an external dependency to every imageless card.
   * Inlined as cream (#FAF7F2) from the brand kit instead.
   */
  const DEEVE_IMAGE_FALLBACK =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' fill='%23FAF7F2'/%3E%3C/svg%3E";

const ShopAIChat = {
    /**
     * UI-related elements and functionality
     */
    UI: {
      elements: {},
      isMobile: false,

      /**
       * Initialize UI elements and event listeners
       * @param {HTMLElement} container - The main container element
       */
      init: function(container) {
        if (!container) return;

        // Cache DOM elements
        this.elements = {
          container: container,
          chatBubble: container.querySelector('.shop-ai-chat-bubble'),
          chatWindow: container.querySelector('.shop-ai-chat-window'),
          closeButton: container.querySelector('.shop-ai-chat-close'),
          chatInput: container.querySelector('.shop-ai-chat-input input'),
          sendButton: container.querySelector('.shop-ai-chat-send'),
          messagesContainer: container.querySelector('.shop-ai-chat-messages')
        };

        // Detect mobile device
        this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        // Set up event listeners
        this.setupEventListeners();

        // Fix for iOS Safari viewport height issues
        if (this.isMobile) {
          this.setupMobileViewport();
        }
      },

      /**
       * Set up all event listeners for UI interactions
       */
      setupEventListeners: function() {
        const { chatBubble, closeButton, chatInput, sendButton, messagesContainer } = this.elements;

        // Toggle chat window visibility
        chatBubble.addEventListener('click', () => this.toggleChatWindow());

        // Close chat window
        closeButton.addEventListener('click', () => this.closeChatWindow());

        // Send message when pressing Enter in input
        chatInput.addEventListener('keypress', (e) => {
          if (e.key === 'Enter' && chatInput.value.trim() !== '') {
            ShopAIChat.Message.send(chatInput, messagesContainer);

            // On mobile, handle keyboard
            if (this.isMobile) {
              chatInput.blur();
              setTimeout(() => chatInput.focus(), 300);
            }
          }
        });

        // Send message when clicking send button
        sendButton.addEventListener('click', () => {
          if (chatInput.value.trim() !== '') {
            ShopAIChat.Message.send(chatInput, messagesContainer);

            // On mobile, focus input after sending
            if (this.isMobile) {
              setTimeout(() => chatInput.focus(), 300);
            }
          }
        });

        // Handle window resize to adjust scrolling
        window.addEventListener('resize', () => this.scrollToBottom());

        // Add global click handler for auth links
        document.addEventListener('click', function(event) {
          if (event.target && event.target.classList.contains('shop-auth-trigger')) {
            event.preventDefault();
            if (window.shopAuthUrl) {
              ShopAIChat.Auth.openAuthPopup(window.shopAuthUrl);
            }
          }
        });

        // Show the SMS terms once, the first time a guest reaches for the text control.
        // Deliberately NOT preventDefault: the notice is a disclosure, not a gate, and the
        // link must still open the guest's messages app if anything here throws.
        const smsLink = this.elements.container.querySelector('[data-deeve-sms]');
        if (smsLink) {
          smsLink.addEventListener('click', () => {
            try {
              ShopAIChat.Message.showSmsNotice(messagesContainer);
            } catch (error) {
              console.error('Could not show the SMS notice:', error);
            }
          });
        }
      },

      /**
       * Setup mobile-specific viewport adjustments
       */
      setupMobileViewport: function() {
        const setViewportHeight = () => {
          document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        };
        window.addEventListener('resize', setViewportHeight);
        setViewportHeight();
      },

      /**
       * Toggle chat window visibility
       */
      toggleChatWindow: function() {
        const { chatWindow, chatInput, container } = this.elements;

        chatWindow.classList.toggle('active');

        // Marks the container while the chat is open so CSS can hide the bubble, which
        // otherwise sits on top of the full-screen mobile window and covered the send
        // button outright. Deliberately NOT gated on isMobile: that is a user-agent sniff,
        // and a narrow window on a desktop browser takes the mobile layout without ever
        // being considered mobile by it.
        container.classList.toggle('deeve-chat-open', chatWindow.classList.contains('active'));

        if (chatWindow.classList.contains('active')) {
          // Whatever opened the window — the bubble, the pill, the keyboard — the offer has
          // been answered and the pill has no further job. Idempotent, so the pill's own
          // click path calling it first costs nothing.
          ShopAIChat.Teaser.hide();

          // On mobile, prevent body scrolling and delay focus
          if (this.isMobile) {
            document.body.classList.add('shop-ai-chat-open');
            setTimeout(() => chatInput.focus(), 500);
          } else {
            chatInput.focus();
          }
          // Always scroll messages to bottom when opening
          this.scrollToBottom();
        } else {
          // Remove body class when closing
          document.body.classList.remove('shop-ai-chat-open');
        }
      },

      /**
       * Close chat window
       */
      closeChatWindow: function() {
        const { chatWindow, chatInput, container } = this.elements;

        chatWindow.classList.remove('active');
        // Brings the bubble back. Unconditional for the same reason it is set that way.
        container.classList.remove('deeve-chat-open');

        // On mobile, blur input to hide keyboard and enable body scrolling
        if (this.isMobile) {
          chatInput.blur();
          document.body.classList.remove('shop-ai-chat-open');
        }
      },

      /**
       * Scroll messages container to bottom
       */
      scrollToBottom: function() {
        const { messagesContainer } = this.elements;
        setTimeout(() => {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }, 100);
      },

      /**
       * Show typing indicator in the chat
       */
      showTypingIndicator: function() {
        const { messagesContainer } = this.elements;

        const typingIndicator = document.createElement('div');
        typingIndicator.classList.add('shop-ai-typing-indicator');
        typingIndicator.innerHTML = '<span></span><span></span><span></span>';
        messagesContainer.appendChild(typingIndicator);
        this.scrollToBottom();
      },

      /**
       * Remove typing indicator from the chat
       */
      removeTypingIndicator: function() {
        const { messagesContainer } = this.elements;

        const typingIndicator = messagesContainer.querySelector('.shop-ai-typing-indicator');
        if (typingIndicator) {
          typingIndicator.remove();
        }
      },

      /**
       * Display product results in the chat
       * @param {Array} products - Array of product data objects
       */
      displayProductResults: function(products) {
        const { messagesContainer } = this.elements;

        // Create a wrapper for the product section
        const productSection = document.createElement('div');
        productSection.classList.add('shop-ai-product-section');
        messagesContainer.appendChild(productSection);

        // Add a header for the product results
        const header = document.createElement('div');
        header.classList.add('shop-ai-product-header');
        header.innerHTML = '<h4>Top Matching Products</h4>';
        productSection.appendChild(header);

        // Create the product grid container
        const productsContainer = document.createElement('div');
        productsContainer.classList.add('shop-ai-product-grid');

        if (!products || !Array.isArray(products) || products.length === 0) {
          productSection.appendChild(productsContainer);
          const noProductsMessage = document.createElement('p');
          noProductsMessage.textContent = "No products found";
          noProductsMessage.style.padding = "10px";
          productsContainer.appendChild(noProductsMessage);
          this.scrollToBottom();
          return;
        }

        products.forEach(product => {
          const productCard = ShopAIChat.Product.createCard(product);
          productsContainer.appendChild(productCard);
        });

        productSection.appendChild(this.buildCarousel(productsContainer, products.length));
        this.scrollToBottom();
      },

      /**
       * Wrap the product track in a one-at-a-time carousel.
       *
       * WHY ONE AT A TIME. The previous layout was a horizontal strip of 180px cards, so a
       * piece of fine jewellery arrived a third of a phone wide, cropped, beside two other
       * cropped pieces. A diamond shown at thumbnail size is asking to be skipped. One
       * square card, dominant, is the same decision the collection pages make: show the
       * piece, then offer the next.
       *
       * The track scrolls natively with scroll-snap, so a swipe works on touch with no
       * gesture handling of our own — the arrows exist for pointer and keyboard, which a
       * swipe-only carousel locks out entirely.
       *
       * @param {HTMLElement} track - the populated .shop-ai-product-grid
       * @param {number} count - number of cards
       * @returns {HTMLElement} the carousel wrapper to append
       */
      buildCarousel: function(track, count) {
        const carousel = document.createElement('div');
        carousel.classList.add('deeve-carousel');
        carousel.appendChild(track);

        // A single result is not a carousel. No arrows, no counter, nothing to operate.
        if (count < 2) return carousel;

        const status = document.createElement('div');
        status.classList.add('deeve-carousel-status');
        status.setAttribute('aria-live', 'polite');

        // Measured, never assumed: the card is width:100% of a track whose width depends on
        // the chat window, which is itself viewport-relative.
        const step = () => track.firstElementChild?.getBoundingClientRect().width || track.clientWidth;
        const indexNow = () => Math.min(count - 1, Math.max(0, Math.round(track.scrollLeft / (step() || 1))));

        const makeArrow = (direction, label, glyph) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.classList.add('deeve-carousel-nav', `deeve-carousel-${direction}`);
          button.setAttribute('aria-label', label);
          button.textContent = glyph;
          button.addEventListener('click', () => {
            const target = Math.min(count - 1, Math.max(0, indexNow() + (direction === 'next' ? 1 : -1)));

            // Plain assignment, NOT scrollBy({behavior:'smooth'}).
            //
            // The smooth-behaviour call was written first and measured second: the click
            // handler fired, and the track did not move a pixel. Smooth scrolling is not
            // guaranteed — it is absent in some engines and suppressed for guests who have
            // asked for reduced motion — and when it is unavailable the scroll is dropped
            // rather than performed instantly. The carousel would have shipped with arrows
            // that do nothing, on exactly the setups least able to swipe instead.
            //
            // Assignment always moves. The CSS animates it where animation is welcome.
            track.scrollLeft = target * step();
            // Called directly as well as on scroll: if the scroll event is coalesced away
            // or never fires, the counter and the disabled states must still be right.
            update();
          });
          return button;
        };

        const prev = makeArrow('prev', 'Previous piece', '‹');
        const next = makeArrow('next', 'Next piece', '›');

        const update = () => {
          const index = indexNow();
          status.textContent = `${index + 1} of ${count}`;
          // Disabled at the ends rather than wrapping: a guest who reaches the last piece
          // should feel the list end, not loop back and wonder if they missed one.
          prev.disabled = index === 0;
          next.disabled = index === count - 1;
        };

        let ticking = false;
        track.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => { ticking = false; update(); });
        });

        carousel.append(prev, next, status);
        update();
        return carousel;
      }
    },

    /**
     * Message handling and display functionality
     */
    Message: {
      /**
       * What a guest sees while the concierge looks something up. Stands in for every
       * tool, names none of them. Keep it a statement of attention, not of machinery —
       * no tool names, no brackets, no progress percentages, ever.
       */
      TOOL_STATUS_TEXT: 'One moment — checking that for you…',

      /**
       * Send a message to the API
       * @param {HTMLInputElement} chatInput - The input element
       * @param {HTMLElement} messagesContainer - The messages container
       */
      send: async function(chatInput, messagesContainer) {
        const userMessage = chatInput.value.trim();
        const conversationId = sessionStorage.getItem('shopAiConversationId');

        // Add user message to chat
        this.add(userMessage, 'user', messagesContainer);

        // Clear input
        chatInput.value = '';

        // Show typing indicator
        ShopAIChat.UI.showTypingIndicator();

        try {
          ShopAIChat.API.streamResponse(userMessage, conversationId, messagesContainer);
        } catch (error) {
          console.error('Error communicating with Claude API:', error);
          ShopAIChat.UI.removeTypingIndicator();
          this.add("Sorry, I couldn't process your request at the moment. Please try again later.", 'assistant', messagesContainer);
        }
      },

      /**
       * Add a message to the chat
       * @param {string} text - Message content
       * @param {string} sender - Message sender ('user' or 'assistant')
       * @param {HTMLElement} messagesContainer - The messages container
       * @returns {HTMLElement} The created message element
       */
      add: function(text, sender, messagesContainer) {
        const messageElement = document.createElement('div');
        messageElement.classList.add('shop-ai-message', sender);

        if (sender === 'assistant') {
          messageElement.dataset.rawText = text;
          ShopAIChat.Formatting.formatMessageContent(messageElement);
        } else {
          messageElement.textContent = text;
        }

        messagesContainer.appendChild(messageElement);
        ShopAIChat.UI.scrollToBottom();

        return messageElement;
      },

      /**
       * Show a quiet status while the concierge consults its tools.
       *
       * The upstream template printed the machinery straight into the conversation —
       * "Calling tool: get_most_recent_order" with an expandable [+] JSON payload of the
       * arguments. A guest was shown this live on 2026-07-31 and the merchant flagged it:
       * a guest of the house is never shown the plumbing.
       *
       * The tool name and its arguments are deliberately DISCARDED, not reformatted or
       * prettified — there is no phrasing of "get_most_recent_order" that belongs in front
       * of a guest, and the arguments can carry the guest's own email or order number.
       * One calm line stands in for every tool the answer needs, and the answer replaces it.
       *
       * @param {string} _toolMessage - Deliberately unused. The parameter is kept so the
       *                                stream handler's call site is unchanged, and named
       *                                with an underscore so no future edit reaches for it.
       * @param {HTMLElement} messagesContainer - The messages container
       */
      addToolUse: function(_toolMessage, messagesContainer) {
        if (!messagesContainer) return;

        // One status line per turn, however many tools a single answer needs — three
        // lookups should not stack three identical notices in front of the guest.
        let statusElement = messagesContainer.querySelector('.deeve-tool-status');
        if (!statusElement) {
          statusElement = document.createElement('div');
          statusElement.classList.add('shop-ai-message', 'assistant', 'deeve-tool-status');
          messagesContainer.appendChild(statusElement);
        }
        statusElement.textContent = ShopAIChat.Message.TOOL_STATUS_TEXT;

        ShopAIChat.UI.scrollToBottom();
      },

      /**
       * Greet the guest in two beats.
       *
       * The greeting's halves do different jobs — the first introduces Bijan, the second
       * invites the guest to look — and as a single block it was four lines of grey on a
       * phone. Split, each one lands.
       *
       * THE DELAY IS THE POINT. Two bubbles painted in the same frame read as a bot
       * double-texting; a person says hello, then adds the second thought. If the second
       * string is missing for any reason, the first is still a complete, correct greeting.
       *
       * @param {HTMLElement} messagesContainer - The messages container
       */
      showWelcome: function(messagesContainer) {
        if (!messagesContainer) return;

        // The theme's copy of the greeting is the OFFLINE FALLBACK, not the source. The
        // server holds the law's wording and decides which register the page calls for —
        // a reader mid-article gets the educator's opening, a shopper gets the butler's.
        // If the backend cannot be reached, the butler's greeting is the safe default:
        // the widget has no way to know it is on an article without being told.
        const config = window.shopChatConfig || {};
        const fallback = [
          config.welcomeMessage ||
            "Welcome to DEEVE. My name is Bijan, and I'll be looking after you.",
          config.welcomeMessageSecond ||
            "Browse as long as you like — when something catches your eye, I'm here.",
        ].filter(Boolean);

        this.fetchGreeting()
          .then((greeting) => {
            const lines = Array.isArray(greeting?.greeting) && greeting.greeting.length
              ? greeting.greeting
              : fallback;
            const beat = Number.isFinite(greeting?.beatMs) ? greeting.beatMs : this.WELCOME_BEAT_MS;
            this.renderGreeting(lines, beat, messagesContainer);

            // The teaser is offered ONLY on the server's say-so. The widget can see
            // config.pageType and could decide for itself that this is an article — and
            // must not: education mode is server-owned end to end, and a teaser sourced
            // from a client-side hint would be exactly the client-authored register the
            // allowlist exists to prevent. No response, no teaser.
            ShopAIChat.Teaser.offer(greeting?.teaser);
          })
          .catch(() => this.renderGreeting(fallback, this.WELCOME_BEAT_MS, messagesContainer));
      },

      /**
       * Ask the server how to open, and tell it which page this is.
       *
       * The page-type value is a HINT ONLY. The server maps it through an allowlist and
       * ignores anything it does not recognise, so nothing here can select a register —
       * it can only describe where the guest is standing.
       */
      fetchGreeting: async function() {
        const config = window.shopChatConfig || {};
        const params = new URLSearchParams();
        if (config.pageType) params.set('page_type', config.pageType);
        if (config.articleHandle) params.set('article_handle', config.articleHandle);

        // The measurement's denominator: the concierge was OFFERED to this reader. Counted
        // once per browser session, so reloading an article does not inflate it.
        const counted = sessionStorage.getItem('deeveConciergeCounted') === 'true';
        if (!counted) params.set('count', '1');

        const response = await fetch(
          `${DEEVE_API_BASE}/concierge/greeting?${params.toString()}`,
          { headers: { 'Accept': 'application/json' } }
        );
        if (!response.ok) throw new Error(`greeting ${response.status}`);
        const greeting = await response.json();

        // Flagged only after a response actually arrived — a failed fetch should leave the
        // impression uncounted rather than silently lose it.
        if (!counted) sessionStorage.setItem('deeveConciergeCounted', 'true');
        return greeting;
      },

      /**
       * Paint a greeting, one bubble per line.
       *
       * THE DELAY IS THE POINT, in both registers. Both greetings now arrive in two beats:
       * the butler's hinges between its two sentences, the educator's on the law's own
       * em-dash. A `beatMs` of 0 still collapses a greeting to a single frame, so a
       * one-line greeting needs no second code path here.
       */
      renderGreeting: function(lines, beatMs, messagesContainer) {
        if (!messagesContainer || !lines.length) return;

        this.add(lines[0], 'assistant', messagesContainer);

        lines.slice(1).forEach((line, index) => {
          setTimeout(() => {
            // The guest may have closed the chat, or a real reply may have arrived, in the
            // meantime — in either case the rest of a greeting is no longer wanted.
            if (!messagesContainer.isConnected) return;
            this.add(line, 'assistant', messagesContainer);
          }, beatMs * (index + 1));
        });
      },

      /** Long enough to read as a second thought, short enough not to feel like latency. */
      WELCOME_BEAT_MS: 700,

      /**
       * Show the SMS terms once per conversation, when the guest taps the text control.
       *
       * The wording is law copy and is quoted verbatim from the theme snippet — the same
       * sentence the Contact page carries. It is a disclosure shown alongside the action,
       * not a confirmation step: the messages app opens either way.
       *
       * @param {HTMLElement} messagesContainer - The messages container
       */
      showSmsNotice: function(messagesContainer) {
        if (!messagesContainer) return;
        if (sessionStorage.getItem('deeveSmsNoticeShown') === 'true') return;

        const notice = window.shopChatConfig?.smsNotice;
        if (!notice) return;

        const element = this.add(notice, 'assistant', messagesContainer);
        if (element) element.classList.add('deeve-sms-notice');

        sessionStorage.setItem('deeveSmsNoticeShown', 'true');
      },

      /**
       * Remove the tool status the moment the concierge has something real to say.
       * Called from every terminal stream event as well as the first content chunk, so
       * the line cannot outlive the lookup it describes even if a turn ends abnormally.
       *
       * @param {HTMLElement} [messagesContainer] - Defaults to the live container.
       */
      clearToolStatus: function(messagesContainer) {
        const container = messagesContainer || document.querySelector('.shop-ai-chat-messages');
        if (!container) return;
        container.querySelectorAll('.deeve-tool-status').forEach((element) => element.remove());
      }
    },

    /**
     * Text formatting and markdown handling
     */
    Formatting: {
      /**
       * Format message content with markdown and links
       * @param {HTMLElement} element - The element to format
       */
      formatMessageContent: function(element) {
        if (!element || !element.dataset.rawText) return;

        const rawText = element.dataset.rawText;

        // Process the text with various Markdown features
        let processedText = rawText;

        // Process Markdown links
        const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        processedText = processedText.replace(markdownLinkRegex, (match, text, url) => {
          // Check if it's an auth URL
          if (url.includes('shopify.com/authentication') &&
             (url.includes('oauth/authorize') || url.includes('authentication'))) {
            // Store the auth URL in a global variable for later use - this avoids issues with onclick handlers
            window.shopAuthUrl = url;
            // Just return normal link that will be handled by the document click handler
            return '<a href="#auth" class="shop-auth-trigger">' + text + '</a>';
          }
          // If it's a checkout link, replace the text
          else if (url.includes('/cart') || url.includes('checkout')) {
            return '<a href="' + url + '" target="_blank" rel="noopener noreferrer">click here to proceed to checkout</a>';
          } else {
            // For normal links, preserve the original text
            return '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + text + '</a>';
          }
        });

        // Convert text to HTML with proper list handling
        processedText = this.convertMarkdownToHtml(processedText);

        // Apply the formatted HTML
        element.innerHTML = processedText;
      },

      /**
       * Convert Markdown text to HTML with list support
       * @param {string} text - Markdown text to convert
       * @returns {string} HTML content
       */
      convertMarkdownToHtml: function(text) {
        text = text.replace(/(\*\*|__)(.*?)\1/g, '<strong>$2</strong>');
        const lines = text.split('\n');
        let currentList = null;
        let listItems = [];
        let htmlContent = '';
        let startNumber = 1;

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const unorderedMatch = line.match(/^\s*([-*])\s+(.*)/);
          const orderedMatch = line.match(/^\s*(\d+)[\.)]\s+(.*)/);

          if (unorderedMatch) {
            if (currentList !== 'ul') {
              if (currentList === 'ol') {
                htmlContent += `<ol start="${startNumber}">` + listItems.join('') + '</ol>';
                listItems = [];
              }
              currentList = 'ul';
            }
            listItems.push('<li>' + unorderedMatch[2] + '</li>');
          } else if (orderedMatch) {
            if (currentList !== 'ol') {
              if (currentList === 'ul') {
                htmlContent += '<ul>' + listItems.join('') + '</ul>';
                listItems = [];
              }
              currentList = 'ol';
              startNumber = parseInt(orderedMatch[1], 10);
            }
            listItems.push('<li>' + orderedMatch[2] + '</li>');
          } else {
            if (currentList) {
              htmlContent += currentList === 'ul'
                ? '<ul>' + listItems.join('') + '</ul>'
                : `<ol start="${startNumber}">` + listItems.join('') + '</ol>';
              listItems = [];
              currentList = null;
            }

            if (line.trim() === '') {
              htmlContent += '<br>';
            } else {
              htmlContent += '<p>' + line + '</p>';
            }
          }
        }

        if (currentList) {
          htmlContent += currentList === 'ul'
            ? '<ul>' + listItems.join('') + '</ul>'
            : `<ol start="${startNumber}">` + listItems.join('') + '</ol>';
        }

        htmlContent = htmlContent.replace(/<\/p><p>/g, '</p>\n<p>');
        return htmlContent;
      }
    },

    /**
     * API communication and data handling
     */
    API: {
      /**
       * Stream a response from the API
       * @param {string} userMessage - User's message text
       * @param {string} conversationId - Conversation ID for context
       * @param {HTMLElement} messagesContainer - The messages container
       */
      streamResponse: async function(userMessage, conversationId, messagesContainer) {
        let currentMessageElement = null;

        try {
          // page_type and article_handle are HINTS. The server maps the page type through
          // a two-entry allowlist and falls back to the butler for anything else, and the
          // handle is only ever a lookup key into the server's own bundled article corpus.
          // The widget never sends article TEXT — the server would ignore it, and a client
          // that could supply prose could have anything summarised in DEEVE's voice.
          const config = window.shopChatConfig || {};
          const requestBody = JSON.stringify({
            message: userMessage,
            conversation_id: conversationId,
            page_type: config.pageType || undefined,
            article_handle: config.articleHandle || undefined,
          });

          const streamUrl = `${DEEVE_API_BASE}/chat`;
          const shopId = window.shopId;

          const conversationToken = sessionStorage.getItem('shopAiConversationToken');

          const response = await fetch(streamUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'text/event-stream',
              'X-Shopify-Shop-Id': shopId,
              ...(conversationToken ? { 'X-Conversation-Token': conversationToken } : {})
            },
            body: requestBody
          });

          // ⛔ 2026-08-23 — this went straight to getReader() with no ok check, unlike the history
          // and greeting fetches. A JSON error body (429, or 403 once enforcement is on) yields
          // ZERO data: frames, the loop ends cleanly, no catch fires, and the typing indicator
          // animates forever under an empty bubble. Silence is the worst failure mode a chat has.
          if (!response.ok) {
            throw new Error(`chat request failed: ${response.status}`);
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          // Create initial message element
          let messageElement = document.createElement('div');
          messageElement.classList.add('shop-ai-message', 'assistant');
          messageElement.textContent = '';
          messageElement.dataset.rawText = '';
          messagesContainer.appendChild(messageElement);
          currentMessageElement = messageElement;

          // Process the stream
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  this.handleStreamEvent(data, currentMessageElement, messagesContainer, userMessage,
                    (newElement) => { currentMessageElement = newElement; });
                } catch (e) {
                  console.error('Error parsing event data:', e, line);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error in streaming:', error);
          ShopAIChat.UI.removeTypingIndicator();
          // A dropped stream must not leave "checking that for you" on screen forever.
          ShopAIChat.Message.clearToolStatus(messagesContainer);
          ShopAIChat.Message.add("Sorry, I couldn't process your request. Please try again later.",
            'assistant', messagesContainer);
        }
      },

      /**
       * Handle stream events from the API
       * @param {Object} data - Event data
       * @param {HTMLElement} currentMessageElement - Current message element being updated
       * @param {HTMLElement} messagesContainer - The messages container
       * @param {string} userMessage - The original user message
       * @param {Function} updateCurrentElement - Callback to update the current element reference
       */
      handleStreamEvent: function(data, currentMessageElement, messagesContainer, userMessage, updateCurrentElement) {
        switch (data.type) {
          case 'id':
            if (data.conversation_id) {
              sessionStorage.setItem('shopAiConversationId', data.conversation_id);
              // ⚖️ 2026-08-23 — a conversation has a SECRET, not an owner. There is no sign-in,
              // so this token is the ONLY proof that this browser created this conversation.
              // Same key, same lifetime as the id: both die when the tab closes, by design.
              if (data.conversation_token) {
                sessionStorage.setItem('shopAiConversationToken', data.conversation_token);
              }
            }
            break;

          case 'chunk':
            ShopAIChat.UI.removeTypingIndicator();
            // The answer has begun — the lookup notice has done its job.
            ShopAIChat.Message.clearToolStatus(messagesContainer);
            currentMessageElement.dataset.rawText += data.chunk;
            currentMessageElement.textContent = currentMessageElement.dataset.rawText;
            ShopAIChat.UI.scrollToBottom();
            break;

          case 'message_complete':
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.Message.clearToolStatus(messagesContainer);
            ShopAIChat.Formatting.formatMessageContent(currentMessageElement);
            ShopAIChat.UI.scrollToBottom();
            break;

          case 'end_turn':
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.Message.clearToolStatus(messagesContainer);
            break;

          case 'error':
            console.error('Stream error:', data.error);
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.Message.clearToolStatus(messagesContainer);
            currentMessageElement.textContent = "Sorry, I couldn't process your request. Please try again later.";
            break;

          case 'rate_limit_exceeded':
            console.error('Rate limit exceeded:', data.error);
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.Message.clearToolStatus(messagesContainer);
            currentMessageElement.textContent = "Sorry, our servers are currently busy. Please try again later.";
            break;

          case 'auth_required':
            // Save the last user message so the answer can be delivered the moment the
            // guest finishes signing in, without their having to ask a second time.
            sessionStorage.setItem('shopAiLastMessage', userMessage || '');
            ShopAIChat.Message.clearToolStatus(messagesContainer);
            break;

          case 'product_results':
            ShopAIChat.UI.displayProductResults(data.products);
            break;

          case 'tool_use':
            if (data.tool_use_message) {
              ShopAIChat.Message.addToolUse(data.tool_use_message, messagesContainer);
            }
            break;

          case 'new_message':
            ShopAIChat.Formatting.formatMessageContent(currentMessageElement);
            ShopAIChat.UI.showTypingIndicator();

            // Create new message element for the next response
            const newMessageElement = document.createElement('div');
            newMessageElement.classList.add('shop-ai-message', 'assistant');
            newMessageElement.textContent = '';
            newMessageElement.dataset.rawText = '';
            messagesContainer.appendChild(newMessageElement);

            // Update the current element reference
            updateCurrentElement(newMessageElement);
            break;

          case 'content_block_complete':
            ShopAIChat.UI.showTypingIndicator();
            break;
        }
      },

      /**
       * Fetch chat history from the server
       * @param {string} conversationId - Conversation ID
       * @param {HTMLElement} messagesContainer - The messages container
       */
      fetchChatHistory: async function(conversationId, messagesContainer) {
        try {
          // Show a loading message
          const loadingMessage = document.createElement('div');
          loadingMessage.classList.add('shop-ai-message', 'assistant');
          loadingMessage.textContent = "Loading conversation history...";
          messagesContainer.appendChild(loadingMessage);

          // Fetch history from the server
          const historyUrl = `${DEEVE_API_BASE}/chat?history=true&conversation_id=${encodeURIComponent(conversationId)}`;
          console.log('Fetching history from:', historyUrl);

          const historyToken = sessionStorage.getItem('shopAiConversationToken');
          const response = await fetch(historyUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              // The endpoint this token exists to protect. Without it, once enforcement is on,
              // history comes back empty rather than 403 — refusal is deliberately shaped like a
              // miss so an attacker cannot use it to learn which conversations are real.
              ...(historyToken ? { 'X-Conversation-Token': historyToken } : {})
            },
            mode: 'cors'
          });

          if (!response.ok) {
            console.error('History fetch failed:', response.status, response.statusText);
            throw new Error('Failed to fetch chat history: ' + response.status);
          }

          const data = await response.json();

          // Remove loading message
          messagesContainer.removeChild(loadingMessage);

          // No messages, show welcome message
          if (!data.messages || data.messages.length === 0) {
            ShopAIChat.Message.showWelcome(messagesContainer);
            return;
          }

          // Add messages to the UI - filter out tool results
          data.messages.forEach(message => {
            try {
              const messageContents = JSON.parse(message.content);
              for (const contentBlock of messageContents) {
                if (contentBlock.type === 'text') {
                  ShopAIChat.Message.add(contentBlock.text, message.role, messagesContainer);
                }
              }
            } catch (e) {
              ShopAIChat.Message.add(message.content, message.role, messagesContainer);
            }
          });

          // Scroll to bottom
          ShopAIChat.UI.scrollToBottom();

        } catch (error) {
          console.error('Error fetching chat history:', error);

          // Remove loading message if it exists
          const loadingMessage = messagesContainer.querySelector('.shop-ai-message.assistant');
          if (loadingMessage && loadingMessage.textContent === "Loading conversation history...") {
            messagesContainer.removeChild(loadingMessage);
          }

          // Show error and welcome message
          ShopAIChat.Message.showWelcome(messagesContainer);

          // Clear the conversation ID since we couldn't fetch this conversation
          sessionStorage.removeItem('shopAiConversationId');
        }
      }
    },

    /**
     * Authentication-related functionality
     */
    Auth: {
      /**
       * Opens an authentication popup window
       * @param {string|HTMLElement} authUrlOrElement - The auth URL or link element that was clicked
       */
      openAuthPopup: function(authUrlOrElement) {
        let authUrl;
        if (typeof authUrlOrElement === 'string') {
          // If a string URL was passed directly
          authUrl = authUrlOrElement;
        } else {
          // If an element was passed
          authUrl = authUrlOrElement.getAttribute('data-auth-url');
          if (!authUrl) {
            console.error('No auth URL found in element');
            return;
          }
        }

        // Open the popup window centered in the screen
        const width = 600;
        const height = 700;
        const left = (window.innerWidth - width) / 2 + window.screenX;
        const top = (window.innerHeight - height) / 2 + window.screenY;

        const popup = window.open(
          authUrl,
          'ShopifyAuth',
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        // Focus the popup window
        if (popup) {
          popup.focus();
        } else {
          // If popup was blocked, show a message
          alert('Please allow popups for this site to authenticate with Shopify.');
        }

        // Start polling for token availability
        const conversationId = sessionStorage.getItem('shopAiConversationId');
        if (conversationId) {
          const messagesContainer = document.querySelector('.shop-ai-chat-messages');

          ShopAIChat.Message.add("I'll be right here while you sign in.",
            'assistant', messagesContainer);

          this.startTokenPolling(conversationId, messagesContainer);

          // The popup closing is the earliest reliable hint that sign-in finished, and it
          // fires even when postMessage is blocked. Check immediately rather than waiting
          // out the polling interval.
          if (popup) {
            const closeWatcher = setInterval(() => {
              if (!popup.closed) return;
              clearInterval(closeWatcher);
              ShopAIChat.Auth.checkTokenAndResume(conversationId, messagesContainer);
            }, 500);

            // Never leave the interval running if the guest abandons the sign-in.
            setTimeout(() => clearInterval(closeWatcher), 5 * 60 * 1000);
          }
        }
      },

      /**
       * Listen for the callback page announcing a completed sign-in.
       *
       * This is what makes the conversation resume the moment the popup closes, rather
       * than on the next poll. Bound once at startup, before any sign-in begins, because
       * the message can arrive at any time afterwards.
       */
      listenForAuthCompletion: function() {
        if (this._authListenerBound) return;
        this._authListenerBound = true;

        window.addEventListener('message', (event) => {
          // Only the concierge backend may trigger a resume. Any page the guest has open
          // can postMessage into this window, so the origin is checked before the payload
          // is even read — an unchecked handler here would let another tab drive the chat.
          let expectedOrigin;
          try {
            expectedOrigin = new URL(DEEVE_API_BASE).origin;
          } catch (error) {
            return;
          }
          if (event.origin !== expectedOrigin) return;

          const data = event.data;
          if (!data || data.source !== 'deeve-concierge' || data.type !== 'auth_complete') return;

          const conversationId = data.conversationId || sessionStorage.getItem('shopAiConversationId');
          if (!conversationId) return;

          const messagesContainer = document.querySelector('.shop-ai-chat-messages');
          this.checkTokenAndResume(conversationId, messagesContainer);
        });
      },

      /**
       * Ask once whether the token has landed, and resume the conversation if it has.
       *
       * Every route into the resume funnels through here — the postMessage announcement,
       * the popup-closed watcher, the poll, and the widget reopening on a fresh page load.
       * It is safe to call repeatedly: `resumeConversation` is guarded so only the first
       * caller to see an authorized token actually sends anything.
       *
       * @param {string} conversationId - Conversation ID
       * @param {HTMLElement} messagesContainer - The messages container
       * @returns {Promise<boolean>} Whether the conversation was resumed
       */
      checkTokenAndResume: async function(conversationId, messagesContainer) {
        if (!conversationId) return false;

        try {
          const tokenUrl = `${DEEVE_API_BASE}/auth/token-status?conversation_id=` +
            encodeURIComponent(conversationId);

          // ⚖️ 2026-08-23 — PHASE 2. The conversation token goes on THIS poll too.
          //
          // `1689b4a` added the header to the chat stream and to history and missed this third
          // call site, so `/auth/token-status` answered on a conversation id alone — the one
          // endpoint of the three `04-CONCIERGE/identity.md` §2 names that never checked.
          //
          // ⛔ THE HEADER MAKES THIS A NON-SIMPLE REQUEST, so the browser now PREFLIGHTS it.
          // The server was fixed first (`7ee1631`): OPTIONS on that route used to return 400,
          // because it answered the preflight in its `action` where React Router never routed
          // it. Shipping this widget against the old server would have killed sign-in resume
          // outright, and it would have looked like an auth failure rather than a CORS one.
          //
          // Same conditional spread as the other two call sites: a session with no token sends
          // no header, and the server serves it during phase 1 rather than refusing.
          const conversationToken = sessionStorage.getItem('shopAiConversationToken');

          const response = await fetch(tokenUrl, {
            headers: {
              'Accept': 'application/json',
              ...(conversationToken ? { 'X-Conversation-Token': conversationToken } : {})
            }
          });
          if (!response.ok) return false;

          const data = await response.json();
          if (data.status !== 'authorized') return false;

          return this.resumeConversation(conversationId, messagesContainer);
        } catch (error) {
          console.error('Error checking token status:', error);
          return false;
        }
      },

      /**
       * Deliver the answer the guest was waiting for, without their having to ask again.
       *
       * The guest asked "where is my order", was sent to sign in, and came back. Making
       * them repeat the question is the defect this exists to prevent (merchant-flagged
       * 2026-07-31).
       *
       * The pending question is removed from sessionStorage BEFORE the send, so that two
       * signals arriving together — the popup closing and the postMessage, say — cannot
       * both resume and ask Claude the same question twice.
       *
       * @param {string} conversationId - Conversation ID
       * @param {HTMLElement} messagesContainer - The messages container
       * @returns {boolean} Whether a resume was started
       */
      resumeConversation: function(conversationId, messagesContainer) {
        const message = sessionStorage.getItem('shopAiLastMessage');
        if (!message) return false;

        // Claim it first — this is the lock that makes the resume happen exactly once.
        sessionStorage.removeItem('shopAiLastMessage');
        sessionStorage.removeItem('shopAiTokenPollingId');

        const container = messagesContainer || document.querySelector('.shop-ai-chat-messages');
        if (!container) return false;

        ShopAIChat.Message.add("Thank you — you're signed in. Let me look that up for you.",
          'assistant', container);
        ShopAIChat.UI.showTypingIndicator();
        ShopAIChat.API.streamResponse(message, conversationId, container);

        return true;
      },

      /**
       * Start polling for token availability
       * @param {string} conversationId - Conversation ID
       * @param {HTMLElement} messagesContainer - The messages container
       */
      startTokenPolling: function(conversationId, messagesContainer) {
        if (!conversationId) return;

        console.log('Starting token polling for conversation:', conversationId);
        const pollingId = 'polling_' + Date.now();
        sessionStorage.setItem('shopAiTokenPollingId', pollingId);

        let attemptCount = 0;

        // Sign-in usually takes 20-60 seconds, so a flat 10-second poll meant the guest
        // could finish and then wait most of another 10 seconds staring at nothing. Poll
        // briskly while they are most likely to be finishing, then back off so a guest who
        // wandered away is not generating requests for five minutes. This covers roughly
        // six minutes in total; postMessage and the popup watcher normally beat it anyway.
        const delayFor = (attempt) => {
          if (attempt <= 10) return 2000;   // first ~20s
          if (attempt <= 25) return 5000;   // out to ~95s
          return 15000;                     // long tail
        };
        const maxAttempts = 45;

        const poll = async () => {
          // A newer sign-in attempt supersedes this one; a completed resume clears the id.
          if (sessionStorage.getItem('shopAiTokenPollingId') !== pollingId) {
            return;
          }

          if (attemptCount >= maxAttempts) {
            console.log('Max polling attempts reached, stopping');
            return;
          }

          attemptCount++;

          const resumed = await ShopAIChat.Auth.checkTokenAndResume(conversationId, messagesContainer);
          if (resumed) return;

          setTimeout(poll, delayFor(attemptCount));
        };

        setTimeout(poll, 1500);
      }
    },

    /**
     * The article teaser — Bijan, once, to a reader who is half way down.
     *
     * ─── WHAT THIS IS NOT ─────────────────────────────────────────────────────────────
     * It does not open the chat. Nothing in this widget opens the chat by itself, and the
     * theme snippet says so in as many words: a reader came to read, and Bijan waits behind
     * the bubble. The pill is a visible offer sitting next to a control the guest may or may
     * not reach for. Every path below that opens the window is a click the guest made.
     *
     * ─── THE THREE RESTRAINTS ─────────────────────────────────────────────────────────
     *   1. HALF WAY DOWN, not on arrival. A reader who has travelled half the article's
     *      scroll range has decided to read it; a reader who just landed has not decided
     *      anything, and an offer at that moment is an interruption.
     *   2. ONCE PER ARTICLE, marked the moment it is SHOWN rather than when dismissed — so
     *      a reload, a back-navigation, or a second visit does not re-offer. Dismissing it
     *      and reloading must not bring it back; that is the difference between an offer
     *      and a nag.
     *   3. ONLY WHEN THE HOUSE SAYS SO. The copy comes from the server, in the law's own
     *      words. The widget owns when, never what.
     *
     * The seen-marks are per article and outlive the tab on purpose: "once per article"
     * read as "once per session" would re-offer on every visit to the same piece.
     */
    Teaser: {
      /** Namespaced per article; the value is only ever "1". */
      SEEN_PREFIX: 'deeveTeaserSeen:',

      /** Half the scrollable range. */
      THRESHOLD: 0.5,

      /**
       * Below this much scrollable range, "half way" is a flick of the wrist and means
       * nothing about whether the guest is reading. An article barely taller than the
       * viewport is one the reader can already see the end of.
       */
      MIN_RANGE_PX: 240,

      element: null,
      onScroll: null,

      /**
       * Offer the teaser, if the house sent one and this article has not had it.
       *
       * @param {unknown} text - the server's teaser copy, or null/absent off an article
       */
      offer: function(text) {
        if (typeof text !== 'string' || !text.trim()) return;

        const key = this.storageKey();
        if (this.seen(key)) return;

        // Nothing to offer a guest who is already talking to him.
        const chatWindow = ShopAIChat.UI.elements.chatWindow;
        if (chatWindow && chatWindow.classList.contains('active')) return;

        // rAF-coalesced: scroll fires far more often than layout can be read, and this
        // handler reads three layout properties.
        let queued = false;
        this.onScroll = () => {
          if (queued) return;
          queued = true;
          window.requestAnimationFrame(() => {
            queued = false;
            if (this.pastThreshold()) this.show(text.trim(), key);
          });
        };

        // Deliberately NOT evaluated once on load. A guest who reloads deep in an article,
        // or follows an anchor link into the middle of one, has not scrolled yet — firing
        // on arrival is the on-landing interruption restraint 1 exists to avoid. They will
        // scroll within seconds, and then it is an offer rather than a pop-up.
        window.addEventListener('scroll', this.onScroll, { passive: true });
      },

      /** True once the guest has travelled past half the article's scrollable range. */
      pastThreshold: function() {
        const doc = document.documentElement;
        const range = doc.scrollHeight - window.innerHeight;
        if (range < this.MIN_RANGE_PX) return false;
        return (window.scrollY || doc.scrollTop || 0) / range >= this.THRESHOLD;
      },

      /**
       * Paint the pill. Marks the article seen immediately — see restraint 2.
       */
      show: function(text, key) {
        this.stopWatching();
        if (this.element) return;

        const container = ShopAIChat.UI.elements.container;
        if (!container) return;

        this.markSeen(key);

        const pill = document.createElement('div');
        pill.className = 'deeve-teaser';

        // Two sibling buttons rather than a button inside a button, which is invalid and
        // makes the dismiss unreachable for a keyboard.
        const open = document.createElement('button');
        open.type = 'button';
        open.className = 'deeve-teaser-open';
        open.textContent = text;
        open.addEventListener('click', () => {
          this.hide();
          // The guest clicked. This is the same path as clicking the bubble.
          ShopAIChat.UI.toggleChatWindow();
        });

        const dismiss = document.createElement('button');
        dismiss.type = 'button';
        dismiss.className = 'deeve-teaser-dismiss';
        dismiss.setAttribute('aria-label', 'Dismiss');
        dismiss.textContent = '✕';
        dismiss.addEventListener('click', () => this.hide());

        pill.appendChild(open);
        pill.appendChild(dismiss);
        container.appendChild(pill);

        // One frame later so the transition has a from-state to animate out of.
        window.requestAnimationFrame(() => pill.classList.add('deeve-teaser-in'));

        this.element = pill;
      },

      /** Remove the pill. Safe to call when there is none — the bubble click path does. */
      hide: function() {
        this.stopWatching();
        if (!this.element) return;
        this.element.remove();
        this.element = null;
      },

      stopWatching: function() {
        if (!this.onScroll) return;
        window.removeEventListener('scroll', this.onScroll);
        this.onScroll = null;
      },

      /**
       * The article this pill belongs to.
       *
       * The handle is the identity where there is one. The server only sends a teaser in
       * article mode, but article mode does not require the handle to have survived
       * sanitising — so the path stands in, which is stable for the same article and
       * distinct between articles, which is all the key has to be.
       */
      storageKey: function() {
        const config = window.shopChatConfig || {};
        return this.SEEN_PREFIX + (config.articleHandle || window.location.pathname);
      },

      /**
       * localStorage, guarded. Safari in private mode throws on write, and a storage
       * failure must cost the guest nothing worse than seeing the offer twice.
       */
      seen: function(key) {
        try {
          return window.localStorage.getItem(key) === '1';
        } catch (error) {
          return false;
        }
      },

      markSeen: function(key) {
        try {
          window.localStorage.setItem(key, '1');
        } catch (error) {
          // Nothing to do: the offer stands, it simply is not remembered.
        }
      }
    },

    /**
     * Product-related functionality
     */
    Product: {
      /**
       * Create a product card element
       * @param {Object} product - Product data
       * @returns {HTMLElement} Product card element
       */
      createCard: function(product) {
        const card = document.createElement('div');
        card.classList.add('shop-ai-product-card');

        // Create image container
        const imageContainer = document.createElement('div');
        imageContainer.classList.add('shop-ai-product-image');

        // Add product image or placeholder
        const image = document.createElement('img');
        image.src = product.image_url || DEEVE_IMAGE_FALLBACK;
        image.alt = product.title;
        image.onerror = function() {
          // If image fails to load, use a fallback placeholder
          this.src = DEEVE_IMAGE_FALLBACK;
        };
        imageContainer.appendChild(image);
        card.appendChild(imageContainer);

        // Add product info
        const info = document.createElement('div');
        info.classList.add('shop-ai-product-info');

        // Add product title
        const title = document.createElement('h3');
        title.classList.add('shop-ai-product-title');
        title.textContent = product.title;

        // If product has a URL, make the title a link
        if (product.url) {
          const titleLink = document.createElement('a');
          titleLink.href = product.url;
          titleLink.target = '_blank';
          titleLink.textContent = product.title;
          title.textContent = '';
          title.appendChild(titleLink);
        }

        info.appendChild(title);

        // Add product price
        const price = document.createElement('p');
        price.classList.add('shop-ai-product-price');
        price.textContent = product.price;
        info.appendChild(price);

        // Add add-to-cart button
        const button = document.createElement('button');
        button.classList.add('shop-ai-add-to-cart');
        button.textContent = 'Add to Cart';
        button.dataset.productId = product.id;

        // Add click handler for the button
        button.addEventListener('click', function() {
          // Send message to add this product to cart
          const input = document.querySelector('.shop-ai-chat-input input');
          if (input) {
            input.value = `Add ${product.title} to my cart`;
            // Trigger a click on the send button
            const sendButton = document.querySelector('.shop-ai-chat-send');
            if (sendButton) {
              sendButton.click();
            }
          }
        });

        info.appendChild(button);
        card.appendChild(info);

        return card;
      }
    },

    /**
     * Initialize the chat application
     */
    init: function() {
      // Initialize UI
      const container = document.querySelector('.shop-ai-chat-container');
      if (!container) return;

      this.UI.init(container);

      // Bound before anything else can start a sign-in, since the announcement from the
      // callback page can arrive at any point afterwards.
      this.Auth.listenForAuthCompletion();

      // Check for existing conversation
      const conversationId = sessionStorage.getItem('shopAiConversationId');

      if (conversationId) {
        // Fetch conversation history
        this.API.fetchChatHistory(conversationId, this.UI.elements.messagesContainer);

        // The guest may have signed in and come back through a full page load — a
        // same-tab redirect, a blocked popup they opened manually, or simply reloading.
        // In that case nothing is polling and no announcement is coming, and without
        // this the question they already asked would go unanswered until they asked it
        // again. History has to settle first so the answer lands beneath it.
        if (sessionStorage.getItem('shopAiLastMessage')) {
          setTimeout(() => {
            this.Auth.checkTokenAndResume(conversationId, this.UI.elements.messagesContainer);
          }, 1200);
        }
      } else {
        // No previous conversation, show welcome message
        this.Message.showWelcome(this.UI.elements.messagesContainer);
      }
    }
  };

  // Initialize the application when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    ShopAIChat.init();
  });
})();
